import {SerializedTableCellNode, TableCellNode} from '@lexical/table';
import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';

import styles from './EnhancedTableCellNode.module.css';
import {$isTableCellDecoratorNode} from './TableCellDecoratorNode';
import {registerTableCellNode, unregisterTableCellNode} from './TableCellNodeUtils';

export interface TableCellSettings {
  guidance?: string;
}

export type SerializedEnhancedTableCellNode = Spread<
  {
    settings: TableCellSettings;
    cellId: string;
  },
  SerializedTableCellNode
>;

function generateTableCellId(): string {
  return `table-cell-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

export class EnhancedTableCellNode extends TableCellNode {
  __settings: TableCellSettings;
  __cellId: string;

  static getType(): string {
    return 'enhanced-tablecell';
  }

  static clone(node: EnhancedTableCellNode): EnhancedTableCellNode {
    return new EnhancedTableCellNode(
      node.getHeaderStyles(),
      node.getColSpan(),
      node.getRowSpan(),
      node.__settings,
      node.__cellId,
      node.__key
    );
  }

  constructor(
    headerStyles: number = 0,
    colSpan: number = 1,
    rowSpan: number = 1,
    settings: TableCellSettings = {},
    cellId?: string,
    key?: NodeKey
  ) {
    super(headerStyles, colSpan, rowSpan, key);
    this.__settings = settings;
    this.__cellId = cellId || generateTableCellId();
  }

  createDOM(config: EditorConfig): HTMLTableCellElement {
    const element = super.createDOM(config);

    // Add data attributes for styling and functionality
    element.setAttribute('data-cell-id', this.__cellId);

    // Add CSS module class for styling
    element.classList.add(styles.tableCell);

    return element;
  }

  updateDOM(prevNode: EnhancedTableCellNode): boolean {
    const isUpdated = super.updateDOM(prevNode as any);
    return isUpdated;
  }

  // Getters and setters for settings
  setSettings(settings: TableCellSettings): void {
    const writableNode = this.getWritable();
    writableNode.__settings = {...writableNode.__settings, ...settings};
  }

  getSettings(): TableCellSettings {
    return this.getLatest().__settings;
  }

  updateSettings(updates: Partial<TableCellSettings>): void {
    const writableNode = this.getWritable();
    writableNode.__settings = {...writableNode.__settings, ...updates};
  }

  // Specific setting getters/setters
  setGuidance(guidance?: string): void {
    this.updateSettings({guidance});
  }

  getGuidance(): string | undefined {
    return this.getSettings().guidance;
  }

  // Cell ID getter and setter
  getCellId(): string {
    return this.getLatest().__cellId;
  }

  setCellId(cellId: string): void {
    const writableNode = this.getWritable();
    writableNode.__cellId = cellId;
  }

  // Override remove to unregister from the registry
  remove(): void {
    unregisterTableCellNode(this.__cellId);
    super.remove();
  }

  // Override replace to clean up when node is replaced
  replace<N extends LexicalNode>(replacement: N, includeChildren?: boolean): N {
    unregisterTableCellNode(this.__cellId);
    return super.replace(replacement, includeChildren);
  }

  static importDOM(): DOMConversionMap {
    const map: DOMConversionMap = {};
    map.th = () => {
      return {
        priority: 2,
        conversion: (): DOMConversionOutput => ({
          node: $createEnhancedTableCellNode(1, 1, 1), // Header cell
        }),
      };
    };
    map.td = () => {
      return {
        priority: 2,
        conversion: (): DOMConversionOutput => ({
          node: $createEnhancedTableCellNode(0, 1, 1), // Regular cell
        }),
      };
    };
    return map;
  }

  exportJSON(): SerializedEnhancedTableCellNode {
    return {
      ...super.exportJSON(),
      type: 'enhanced-tablecell',
      settings: this.__settings,
      cellId: this.__cellId,
    };
  }

  static importJSON(serializedNode: SerializedEnhancedTableCellNode): EnhancedTableCellNode {
    const {headerState = 0, colSpan = 1, rowSpan = 1, settings = {}, cellId} = serializedNode;
    const node = $createEnhancedTableCellNode(headerState, colSpan, rowSpan, settings, cellId);

    return node;
  }
}

export function $createEnhancedTableCellNode(
  headerStyles: number = 0,
  colSpan: number = 1,
  rowSpan: number = 1,
  settings: TableCellSettings = {},
  cellId?: string,
  key?: NodeKey
): EnhancedTableCellNode {
  const node = $applyNodeReplacement(
    new EnhancedTableCellNode(headerStyles, colSpan, rowSpan, settings, cellId, key) as any
  ) as EnhancedTableCellNode;

  // Register the node in the registry when created (only once)
  const finalCellId = node.getCellId();
  const nodeKey = node.getKey();

  // Only register if not already registered to prevent duplicates
  try {
    registerTableCellNode(finalCellId, nodeKey);
  } catch {
    // Registration might fail if already exists, which is fine
    console.debug('Table cell node already registered:', finalCellId);
  }

  return node;
}

export function $isEnhancedTableCellNode(node: unknown): node is EnhancedTableCellNode {
  return node instanceof EnhancedTableCellNode;
}

// Helper to convert regular TableCellNode to EnhancedTableCellNode
export function $convertToEnhancedTableCellNode(
  tableCellNode: TableCellNode,
  settings: TableCellSettings = {},
  cellId?: string
): EnhancedTableCellNode {
  const enhancedNode = $createEnhancedTableCellNode(
    tableCellNode.getHeaderStyles(),
    tableCellNode.getColSpan(),
    tableCellNode.getRowSpan(),
    settings,
    cellId
  );

  // Copy all children, but filter out any existing decorator nodes to prevent duplication
  const children = tableCellNode.getChildren();
  children.forEach((child) => {
    // Only copy non-decorator nodes to prevent duplication
    if (!$isTableCellDecoratorNode(child)) {
      enhancedNode.append(child);
    }
  });

  return enhancedNode;
}
